import { ReactElement } from "react";
import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { MainLayout } from "~/components/layouts/MainLayout";
import { StandardServicePage } from "~/components/service-pages/StandardServicePage";
import { GasSafetyCheckContent as content } from "~/content/przeglad-instalacji-gazowej/content.pl";
import type { City } from "~/lib/cities";
import { getCitiesWithServiceOffers } from "~/lib/firms.server";
import { getRecentPostsForService, type PostMeta } from "~/lib/mdx";
import { getServiceBySlug } from "~/lib/services";
import { NextPageWithLayout } from "~/pages/_app";
import gasDetailImage from "~/src/assets/images/gas_10.jpg";
import gasSafetyMaintenanceImage from "~/src/assets/images/hero-power-box.webp";
import gasSafetyChecklistImage from "~/src/assets/images/services-details-gas.webp";

type Props = {
  recentPosts: PostMeta[];
  availableCities: City[];
};

const serviceSlug = "przeglad-instalacji-gazowej";
const service = getServiceBySlug(serviceSlug);

if (!service) {
  throw new Error(`Service not found: ${serviceSlug}`);
}

const pageUrl = `${(
  process.env.NEXT_PUBLIC_BASE_URL ?? "https://inspektomat.pl"
).replace(/\/+$/, "")}${service.href}`;

const sectionImages = [
  {
    src: gasSafetyMaintenanceImage,
    alt: "Kontrola instalacji gazowej i warunków bezpiecznego użytkowania",
  },
  {
    src: gasDetailImage,
    alt: "Sprawdzenie połączeń i armatury instalacji gazowej",
  },
  {
    src: gasSafetyChecklistImage,
    alt: "Protokół z przeglądu instalacji gazowej",
  },
] as const;

const GasSafetyCheckPage: NextPageWithLayout<Props> = ({
  recentPosts,
  availableCities,
}) => {
  return (
    <StandardServicePage
      serviceSlug={serviceSlug}
      content={content}
      sectionImages={sectionImages}
      recentPosts={recentPosts}
      availableCities={availableCities}
    />
  );
};

GasSafetyCheckPage.getLayout = function GetLayout(page: ReactElement) {
  return (
    <MainLayout>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
        openGraph={{
          title: content.seo.title,
          description: content.seo.description,
          url: pageUrl,
        }}
      />
      {page}
    </MainLayout>
  );
};

export default GasSafetyCheckPage;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      recentPosts: getRecentPostsForService(serviceSlug),
      availableCities: getCitiesWithServiceOffers(serviceSlug),
    },
  };
};
